export type FieldErrors = Record<string, string>;

/** Error raised for every non-2xx API response (or network failure). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: FieldErrors;
  readonly requestId?: string;
  readonly retryAfterSeconds?: number;
  /** Structured context from dss_response.details (e.g. affected appointments). */
  readonly details?: Record<string, unknown>;

  constructor(options: {
    status: number;
    code: string;
    message: string;
    fieldErrors?: FieldErrors;
    requestId?: string;
    retryAfterSeconds?: number;
    details?: Record<string, unknown>;
  }) {
    super(options.message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.fieldErrors = options.fieldErrors ?? {};
    this.requestId = options.requestId;
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.details = options.details;
  }

  get isNetworkError() {
    return this.status === 0;
  }
}

/** Stable backend response codes the UI branches on. */
export const API_CODES = {
  TOKEN_EXPIRED: "CP-AUTH-TOKEN-EXPIRED",
  SESSION_REVOKED: "CP-AUTH-SESSION-REVOKED",
  REFRESH_INVALID: "CP-AUTH-REFRESH-INVALID",
  ACCOUNT_LOCKED: "CP-AUTH-ACCOUNT-LOCKED",
  ACCOUNT_NOT_FOUND: "CP-AUTH-ACCOUNT-NOT-FOUND",
  PASSWORD_LOGIN_REQUIRED: "CP-AUTH-PASSWORD-LOGIN-REQUIRED",
  OTP_INVALID: "CP-OTP-INVALID",
  OTP_EXPIRED: "CP-OTP-EXPIRED",
  OTP_ATTEMPTS_EXCEEDED: "CP-OTP-ATTEMPTS-EXCEEDED",
  OTP_COOLDOWN: "CP-OTP-COOLDOWN",
  VERSION_CONFLICT: "CP-VERSION-CONFLICT",
  SCHEDULE_CONFLICT: "CP-SCHEDULE-CONFLICT",
  SLOT_UNAVAILABLE: "CP-SLOT-UNAVAILABLE",
  EMAIL_TAKEN: "CP-USER-EMAIL-TAKEN",
  MOBILE_TAKEN: "CP-USER-MOBILE-TAKEN",
} as const;

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** Human-readable message for any thrown value. */
export function errorMessage(error: unknown, fallback: string): string {
  if (isApiError(error) && error.message) return error.message;
  return fallback;
}
